/**
 * The alert strip on the executive screen (REQ-021 AC-007).
 *
 * Three statuses, drawn three different ways, and the difference between them
 * is the whole point of the component. A firing condition says what is wrong,
 * names the role that owns it, and links to the queue where the work is done —
 * the link comes from the payload, because the server holds the closed list of
 * routes an alert may point at and an invented one would name an action and
 * then lose the person looking for it.
 *
 * A condition nobody can evaluate is drawn as loudly as a firing one and never
 * as an absence. Four of the seven are unevaluable today — no MRR snapshot, no
 * churn transitions, no recorded first response, no bank balance — and a strip
 * that folded them into "all clear" would be `healthpass_active_sessions` a
 * second time: reassuring, and reassuring about nothing.
 *
 * Clear is the boring state and is allowed to be a count. It is still stated,
 * because an empty strip and a strip that failed to load look identical, and
 * that is precisely the confusion the requirement exists to prevent.
 */
import {
  AlertOctagon,
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
  partitionAlerts,
  severityLabel,
  type AlertOutcome,
  type AlertsMeta,
} from "./alerts";

const badge =
  "inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium";

function Owner({ alert }: { alert: AlertOutcome }) {
  return (
    <p className="mt-1 text-xs">
      Owned by <span className="font-medium">{alert.owner}</span>
    </p>
  );
}

function FiringAlert({ alert }: { alert: AlertOutcome }) {
  return (
    <li
      data-testid={`alert-${alert.condition}`}
      data-status="firing"
      className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-900"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 font-medium">
          <AlertOctagon className="h-4 w-4 shrink-0" aria-hidden="true" />
          {alert.label}
        </p>
        <span className={`${badge} bg-red-100 text-red-800`}>
          {severityLabel(alert.severity)}
        </span>
      </div>

      <p className="mt-1 text-xs">{alert.message}</p>
      <Owner alert={alert} />

      {/* The route is the payload's. Nothing on this screen decides where an
          alert leads — the server keeps the list of queues that exist, and
          where it sends no queue it sends the reason instead. A plausible link
          to a page that cannot resolve the alert would be the worse of the two
          by far: it looks answered. */}
      {alert.workQueue ? (
        <Link
          to={alert.workQueue}
          className="mt-2 inline-block text-xs font-medium text-red-800 underline hover:text-red-950"
        >
          Open the work queue ({alert.workQueue})
        </Link>
      ) : (
        <p className="mt-2 text-xs font-medium">
          No work queue resolves this yet. {alert.workQueueGap}
        </p>
      )}
    </li>
  );
}

function UnevaluableAlert({ alert }: { alert: AlertOutcome }) {
  return (
    <li
      data-testid={`alert-${alert.condition}`}
      data-status="unevaluable"
      className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="flex items-center gap-1.5 font-medium">
          <HelpCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
          {alert.label}
        </p>
        <span className={`${badge} bg-amber-200 text-amber-900`}>
          Cannot be evaluated
        </span>
      </div>

      <p className="mt-1 text-xs">{alert.reason}</p>
      <Owner alert={alert} />
    </li>
  );
}

export default function AlertStrip({
  alerts,
  meta,
  isLoading,
  isError,
}: {
  alerts: AlertOutcome[];
  meta?: AlertsMeta;
  isLoading: boolean;
  isError: boolean;
}) {
  const { firing, unevaluable, clear } = partitionAlerts(alerts);
  const total = alerts.length;

  return (
    <section data-testid="alerts-strip" aria-label="Alert conditions">
      {isLoading && (
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading the alert
          conditions…
        </div>
      )}

      {/* A failed read is a failed read. Reporting it as "no alerts" would be
          the most dangerous sentence this screen could say. */}
      {isError && (
        <div
          data-testid="alerts-error"
          className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <p>
            The alert conditions could not be read. This is not the same as
            having no alerts — nothing has been evaluated, and a firing
            condition would look exactly like this.
          </p>
        </div>
      )}

      {!isLoading && !isError && total === 0 && (
        <div
          data-testid="alerts-none"
          className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
        >
          No alert condition reported a standing. That is a gap in the alert
          registry, not a quiet company.
        </div>
      )}

      {!isError && total > 0 && (
        <div className="space-y-3">
          <ul className="grid gap-3 lg:grid-cols-2">
            {firing.map((alert) => (
              <FiringAlert key={alert.condition} alert={alert} />
            ))}
            {unevaluable.map((alert) => (
              <UnevaluableAlert key={alert.condition} alert={alert} />
            ))}
          </ul>

          {clear.length === total ? (
            <p
              data-testid="alerts-all-clear"
              className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              All {total} conditions are clear. Nothing is waiting for a person.
            </p>
          ) : (
            clear.length > 0 && (
              <p
                data-testid="alerts-clear-summary"
                className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 text-xs text-gray-600"
              >
                <CheckCircle2
                  className="h-3.5 w-3.5 shrink-0 text-emerald-600"
                  aria-hidden="true"
                />
                {clear.length} of {total} conditions clear.
              </p>
            )
          )}

          {/* Counted against the conditions that reported, not against the
              seven AC-007 names: an alert nobody owns is never created, so it
              is missing from `count` and would otherwise vanish between the two
              numbers. Its absence gets a line of its own instead. */}
          {meta && meta.unevaluable > 0 && (
            <p
              data-testid="alerts-unevaluable-summary"
              className="text-xs text-gray-500"
            >
              {meta.unevaluable} of {meta.count} conditions have no source to be
              evaluated from. They have never been able to fire.
            </p>
          )}

          {meta && meta.count < meta.conditionLimit && (
            <p data-testid="alerts-ownerless" className="text-xs text-gray-500">
              {meta.conditionLimit - meta.count} of the {meta.conditionLimit}{" "}
              conditions raised nothing because no role owns them. An alert
              nobody owns is not created, so it is not on this strip at all.
            </p>
          )}
        </div>
      )}
    </section>
  );
}
